import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, idr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { DatePicker } from "../../components/ui/date-picker";
import LoadingState from "../../components/ui/loading-state";
import { formatTanggal, hariIniISO } from "../../lib/tanggal";
import InputRupiah from "../../components/InputRupiah";
import AttachmentPanel from "./AttachmentPanel";
import { useSortState, sortRows, SortTh } from "../../components/ui/sort";
import { BatasDaftarNotice } from "./_shared";

// `_get_items` (backend cashback_claim.py) mengurut imei asc lalu memotong
// `to_list(5000)` per batch — nyaris mustahil kesentuh untuk satu Claim
// Batch, tapi tetap didampingi `BatasDaftarNotice` demi konsisten.
const CB_ITEMS_BATAS = 5000;
import {
  ArrowLeft, Smartphone, Send, CheckCircle2, Ban, Wallet, RefreshCcw,
  Paperclip, History, PlusCircle, Trash2, AlertTriangle, ExternalLink, Search, Upload, FileCheck2,
} from "lucide-react";
import {
  CB_BASE, Modal, Field, inputCls, ReasonModal, ActionBtn,
  ClaimStatusPill, ItemStatusPill, DeadlineBadge, PeriodeKlaimBadge,
  JENIS_AKTIVASI, KANAL_TOKO, KANAL_DEALER, labelJenis, labelKanal, ruteHalamanPP, tautanProgram, RUTE_PROGRAM_LAMA,
} from "./CashbackProgram";

// Status tempat settlement boleh dicatat — SAMA PERSIS dengan
// `BATCH_STATUS_BOLEH_SETTLEMENT` di backend (`cashback_claim.py`). Murni
// gerbang tampilan; backend tetap menolak sendiri kalau ada yang lolos.
const STATUS_BOLEH_SETTLEMENT = ["diputuskan", "settlement_sebagian"];
const STATUS_FINAL = ["settled", "closed", "dibatalkan"];
// Role yang boleh mengedit daftar IMEI batch SETELAH disubmit — SAMA PERSIS
// dengan `_pastikan_boleh_edit_item_batch` di backend.
const ROLE_EDIT_AFTER_SUBMIT = ["finance_spv", "finance_ceo", "admin", "superadmin"];

export default function CashbackClaimDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, can, branding } = useAuth();
  const namaToko = branding?.nama_toko_sendiri || "EppleHub";

  const [claim, setClaim] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [reload, setReload] = useState(0);
  const bump = () => setReload((n) => n + 1);
  const [tab, setTab] = useState("overview");
  const [modalKey, setModalKey] = useState(null);
  const [voidTarget, setVoidTarget] = useState(null);
  const [showAtt, setShowAtt] = useState(false);
  const [busy, setBusy] = useState(false);

  const canUpdate = can("finance_cashback_claim", "update");
  const canDelete = can("finance_cashback_claim", "delete");

  useEffect(() => {
    let batal = false;
    setLoading(true); setLoadError(null);
    (async () => {
      try {
        const r = await api.get(`${CB_BASE}/claims/${id}`);
        if (!batal) { setClaim(r.data.item); setItems(r.data.items || []); }
      } catch (e) {
        if (!batal) { setLoadError(e); toast.error(e?.response?.data?.detail || "Gagal memuat Claim Batch"); }
      } finally { if (!batal) setLoading(false); }
    })();
    return () => { batal = true; };
  }, [id, reload]);

  const act = async (fn, okMsg) => {
    setBusy(true);
    try {
      const r = await fn();
      toast.success(r?.data?.message || okMsg);
      bump();
      return r;
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memproses"); return null; }
    finally { setBusy(false); }
  };

  const doBatalkan = (reason) => act(() => api.post(`${CB_BASE}/claims/${id}/batalkan`, { reason }), "Claim Batch dibatalkan ✓");
  const doVoidSettlement = (reason) => act(() => api.post(`${CB_BASE}/claims/${id}/settlement/${voidTarget.id}/void`, { reason }), "Settlement di-void ✓");

  if (loading || loadError || !claim) {
    return (
      <div className="p-4 md:p-8">
        <LoadingState loading={loading} error={loadError} onRetry={bump} varian="kartu" jumlahKartu={3} data-testid="cb-claimdet-loading" />
      </div>
    );
  }

  const outstanding = Math.max(0, round2((claim.total_disetujui || 0) - (claim.nominal_settled || 0)));
  const adaItemSubmitted = items.some((i) => i.status === "submitted");
  const rejectedBisaReclaim = items.filter((i) => i.status === "rejected" && !i.reclaimed_in_item_id);
  const bolehEditItems = !STATUS_FINAL.includes(claim.status) && canUpdate &&
    (claim.status === "draft" || ROLE_EDIT_AFTER_SUBMIT.includes(user?.role));
  const bolehBatalkan = !STATUS_FINAL.includes(claim.status) && canDelete && (claim.nominal_settled || 0) <= 0.5;
  // Batch program ber-jenis (brief 22 Sep 2026) menyalin jenis & kanal programnya;
  // batch lama (dan batch dari program lama, kanal "semua") = cashback lama.
  const programBerKanal = [KANAL_TOKO, KANAL_DEALER].includes(claim.kanal_program);
  const jenisBatch = claim.jenis_program || "cashback";
  const aktivasi = programBerKanal && jenisBatch === JENIS_AKTIVASI;
  const ruteProgram = programBerKanal ? ruteHalamanPP(jenisBatch, claim.kanal_program) : RUTE_PROGRAM_LAMA;

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="finance-cashback-claim-detail">
      <button onClick={() => navigate("/finance/cashback/claims")} data-testid="cb-claimdet-back"
              className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1.5">
        <ArrowLeft className="w-4 h-4" /> Kembali ke Daftar Cashback Claim
      </button>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
        <div className="flex items-start justify-between flex-col md:flex-row gap-3">
          <div>
            <p className="font-mono text-xs text-slate-500">{claim.code}</p>
            <h1 className="font-display text-xl md:text-2xl font-bold text-slate-900 mt-1 flex items-center gap-2">
              <Smartphone className="w-6 h-6 text-emerald-600" /> {claim.supplier_nama}
            </h1>
            <p className="text-sm text-slate-600 mt-1">{claim.program_nama} ({claim.program_code}){claim.brand ? ` · ${claim.brand}` : ""}{claim.cabang ? ` · ${claim.cabang}` : ""}</p>
            <div className="flex items-center gap-2 flex-wrap mt-1.5">
              {programBerKanal && (
                <span className="text-[11px] px-2 py-0.5 rounded-md bg-slate-900 text-white font-medium" data-testid="cb-claimdet-jenis">
                  {labelJenis(jenisBatch)} · {labelKanal(claim.kanal_program, namaToko)}
                </span>
              )}
              <button onClick={() => navigate(tautanProgram(ruteProgram, claim.program_id))} data-testid="cb-claimdet-buka-program"
                      className="text-[11px] text-emerald-700 hover:text-emerald-900 font-medium inline-flex items-center gap-1">
                Buka program <ExternalLink className="w-3 h-3" />
              </button>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <ClaimStatusPill status={claim.status} />
            <DeadlineBadge deadline={claim.deadline_submit_saat_dibuat} statusBatch={claim.status} submittedAfterDeadline={claim.submitted_after_deadline} />
            <PeriodeKlaimBadge diLuarPeriodeKlaim={claim.di_luar_periode_klaim} alasan={claim.di_luar_periode_klaim_alasan} />
          </div>
        </div>

        {claim.status === "dibatalkan" && (
          <div className="mt-4 bg-rose-50 border border-rose-200 rounded-lg p-3 text-sm text-rose-800" data-testid="cb-claimdet-cancel-info">
            <p className="font-semibold">Dibatalkan oleh {claim.batal_by} pada {claim.batal_at ? formatTanggal(claim.batal_at, { denganJam: true }) : "-"}</p>
            <p className="mt-1">{claim.batal_reason}</p>
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mt-4">
          <StatBox label="Jumlah IMEI" value={claim.jumlah_item} />
          <StatBox label="Total Klaim" value={idr(claim.total_klaim)} />
          <StatBox label="Disetujui" value={idr(claim.total_disetujui)} tone="text-emerald-700" />
          <StatBox label="Ditolak" value={idr(claim.total_ditolak)} tone="text-rose-700" />
          <StatBox label="Settled" value={idr(claim.nominal_settled)} tone="text-emerald-700" />
          <StatBox label="Outstanding" value={idr(outstanding)} tone="text-slate-900" big />
        </div>

        <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-slate-100">
          {claim.status === "draft" && canUpdate && (
            <ActionBtn icon={Send} label="Submit ke Supplier" onClick={() => setModalKey("submit")} testid="cb-claimdet-act-submit" primary disabled={busy} />
          )}
          {bolehEditItems && (
            <ActionBtn icon={PlusCircle} label="Kelola IMEI" onClick={() => setModalKey("items")} testid="cb-claimdet-act-items" disabled={busy} />
          )}
          {adaItemSubmitted && canUpdate && (
            <ActionBtn icon={CheckCircle2} label="Putuskan Approve/Reject" onClick={() => setModalKey("putuskan")} testid="cb-claimdet-act-putuskan" primary disabled={busy} />
          )}
          {STATUS_BOLEH_SETTLEMENT.includes(claim.status) && outstanding > 0.5 && canUpdate && (
            <ActionBtn icon={Wallet} label="Catat Settlement" onClick={() => setModalKey("settlement")} testid="cb-claimdet-act-settlement" primary disabled={busy} />
          )}
          {rejectedBisaReclaim.length > 0 && canUpdate && (
            <ActionBtn icon={RefreshCcw} label={`Reclaim ${rejectedBisaReclaim.length} IMEI Ditolak`} onClick={() => setModalKey("reclaim")} testid="cb-claimdet-act-reclaim" disabled={busy} />
          )}
          {bolehBatalkan && (
            <ActionBtn icon={Ban} label="Batalkan Batch" onClick={() => setModalKey("batalkan")} testid="cb-claimdet-act-batalkan" tone="rose" disabled={busy} />
          )}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
        <div className="border-b border-slate-100 flex items-center gap-1 px-2 overflow-x-auto">
          {[
            { key: "overview", label: "Overview" },
            { key: "items", label: `IMEI (${items.length})` },
            { key: "settlement", label: "Settlement" },
            { key: "lampiran", label: "Lampiran" },
            { key: "activity", label: "Activity Log" },
          ].map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`cb-claimdet-tab-${t.key}`}
                    className={`px-4 h-11 text-sm font-medium border-b-2 whitespace-nowrap transition ${tab === t.key ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="p-5">
          {tab === "overview" && (
            <div className="space-y-4">
              {claim.submitted_after_deadline && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 inline-flex items-start gap-2" data-testid="cb-claimdet-warn-deadline">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> Batch ini disubmit SETELAH deadline program ({formatTanggal(claim.deadline_submit_saat_dibuat)}) — supplier berhak menolak klaim ini karena lewat batas waktu.
                </div>
              )}
              {claim.di_luar_periode_klaim && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 inline-flex items-start gap-2" data-testid="cb-claimdet-warn-periode-klaim">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> Batch ini dibuat/ditambah DI LUAR periode klaim program — {claim.di_luar_periode_klaim_alasan || "periode klaim tidak cocok"}. Klaim tetap tersimpan, tapi cek dulu apakah supplier masih mau memprosesnya.
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InfoRow label="Program" value={`${claim.program_code} · ${claim.program_nama}`} />
                <InfoRow label="Supplier" value={claim.supplier_nama} />
                <InfoRow label="Tanggal Submit" value={claim.tanggal_submit ? formatTanggal(claim.tanggal_submit) : "Belum disubmit"} />
                <InfoRow label="Deadline Submit Program" value={formatTanggal(claim.deadline_submit_saat_dibuat)} />
                <InfoRow label="Dibuat oleh" value={`${claim.created_by_name || claim.created_by || "-"} · ${formatTanggal(claim.created_at, { denganJam: true })}`} />
                {claim.keterangan && <InfoRow label="Keterangan" value={claim.keterangan} col2 />}
              </div>
            </div>
          )}

          {tab === "items" && <ItemsTab items={items} aktivasi={aktivasi} programBerKanal={programBerKanal} />}

          {tab === "settlement" && (
            <SettlementTab claim={claim} canDelete={canDelete} onVoid={(s) => setVoidTarget(s)} navigate={navigate} />
          )}

          {tab === "lampiran" && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">Lampirkan bukti kirim ke supplier, konfirmasi approval, atau bukti settlement.</p>
              <button onClick={() => setShowAtt(true)} data-testid="cb-claimdet-open-attachments"
                      className="h-10 px-4 rounded-lg border border-emerald-200 text-emerald-700 hover:bg-emerald-50 text-sm font-medium inline-flex items-center gap-2">
                <Paperclip className="w-4 h-4" /> Kelola Dokumen Lampiran
              </button>
            </div>
          )}

          {tab === "activity" && (
            <div className="space-y-2" data-testid="cb-claimdet-activity-log">
              {[...(claim.riwayat_status || [])].sort((a, b) => (b.at || "").localeCompare(a.at || "")).map((r, i) => (
                <div key={i} data-testid={`cb-claimdet-log-row-${i}`} className="flex items-start gap-3 border-b border-slate-50 pb-2 last:border-0">
                  <History className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <ClaimStatusPill status={r.status} />
                      <span className="text-xs text-slate-500">{formatTanggal(r.at, { denganJam: true })}</span>
                    </div>
                    <p className="text-xs text-slate-600 mt-0.5">oleh {r.by}{r.catatan ? ` — ${r.catatan}` : ""}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {showAtt && (
        <AttachmentPanel module="cashback_claim" objId={claim.id} objLabel={`${claim.code} · ${claim.supplier_nama}`}
                          onClose={() => { setShowAtt(false); bump(); }} />
      )}

      {modalKey === "submit" && (
        <SubmitModal claim={claim} onClose={() => setModalKey(null)} onDone={() => { setModalKey(null); bump(); }} />
      )}
      {modalKey === "items" && (
        <KelolaImeiModal claim={claim} items={items}
                         onClose={() => setModalKey(null)} onDone={() => { setModalKey(null); bump(); }} />
      )}
      {modalKey === "putuskan" && (
        <PutuskanModal claim={claim} items={items.filter((i) => i.status === "submitted")} aktivasi={aktivasi}
                       onClose={() => setModalKey(null)} onDone={() => { setModalKey(null); bump(); }} />
      )}
      {modalKey === "settlement" && (
        <SettlementModal claim={claim} outstanding={outstanding} jenisBatch={jenisBatch} onClose={() => setModalKey(null)} onDone={() => { setModalKey(null); bump(); }} />
      )}
      {modalKey === "reclaim" && (
        <ReclaimModal claim={claim} items={rejectedBisaReclaim} onClose={() => setModalKey(null)}
                     onDone={(newBatch) => { setModalKey(null); if (newBatch) navigate(`/finance/cashback/claims/${newBatch.id}`); else bump(); }} />
      )}
      {modalKey === "batalkan" && (
        <ReasonModal title="Batalkan Claim Batch" icon={Ban} label="Alasan pembatalan" submitLabel="Batalkan Batch"
                     testid="cb-claimdet-batalkan-modal" onClose={() => setModalKey(null)}
                     onSubmit={async (reason) => { const r = await doBatalkan(reason); if (r) setModalKey(null); }} />
      )}
      {voidTarget && (
        <ReasonModal title="Void Settlement" icon={Ban} label="Alasan void" submitLabel="Void Settlement"
                     testid="cb-claimdet-void-modal" onClose={() => setVoidTarget(null)}
                     onSubmit={async (reason) => { const r = await doVoidSettlement(reason); if (r) setVoidTarget(null); }} />
      )}
    </div>
  );
}

function round2(n) { return Math.round((n + Number.EPSILON) * 100) / 100; }

// ============ Sub-komponen header ============
function StatBox({ label, value, tone, big }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <p className={`font-display font-bold mt-0.5 ${big ? "text-xl" : "text-lg"} ${tone || "text-slate-800"}`}>{value}</p>
    </div>
  );
}

function InfoRow({ label, value, col2 }) {
  return (
    <div className={col2 ? "md:col-span-2" : ""}>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <p className="text-sm text-slate-800 mt-0.5">{value}</p>
    </div>
  );
}

// ============ Tab: IMEI (brief butir 9 & 15 — item DITOLAK tetap terlihat,
// TIDAK PERNAH dihapus dari layar, lengkap dengan alasan eligibility saat
// diklaim supaya bisa diaudit). ============
function ItemsTab({ items, aktivasi, programBerKanal }) {
  // Urutan bawaan (brief butir 9 & 15): item DITOLAK tampil paling atas
  // supaya tidak tenggelam saat diaudit, baru diurut IMEI. `sortKey` null =
  // urutan ini yang tampil; klik header lain mengganti urutan (poin
  // pengurutan Finance 21 Agu 2026) SAMPAI diklik lagi untuk kembali null.
  const defaultSorted = [...items].sort((a, b) => (a.status === "rejected" ? -1 : 0) - (b.status === "rejected" ? -1 : 0) || a.imei.localeCompare(b.imei));
  const { sortKey, sortDir, toggle } = useSortState(null);
  const sorted = sortRows(defaultSorted, sortKey, sortDir, {
    imei: (it) => it.imei || "",
    produk: (it) => it.nama_produk || "",
    terjual: (it) => it.tanggal_terjual || "",
    klaim: (it) => it.cashback_nominal || 0,
    disetujui: (it) => it.approved_nominal ?? -1,
  });
  // Kolom Modal DIHAPUS (brief 22 Sep 2026: "Modal (DIHAPUS)" di Support
  // Aktivasi, diganti STP Promo di Cashback) — termasuk untuk batch lama. Modal
  // per unit adalah data E28 (Akses Modal & Margin), bukan kolom klaim.
  const labelKlaim = aktivasi ? "Nominal Support Aktivasi" : "Cashback Klaim";
  return (
    <div className="space-y-2">
      <BatasDaftarNotice shown={items.length} batas={CB_ITEMS_BATAS} />
      <div className="border border-slate-200 rounded-lg overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
          <tr>
            <SortTh label="IMEI" sortKeyName="imei" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="cb-claimdet-sort-imei" />
            {programBerKanal && <th className="text-left px-3 py-2">SN</th>}
            <SortTh label="Produk" sortKeyName="produk" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="cb-claimdet-sort-produk" />
            {programBerKanal && <SortTh label="Terjual" sortKeyName="terjual" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="cb-claimdet-sort-terjual" />}
            <SortTh label={labelKlaim} sortKeyName="klaim" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="cb-claimdet-sort-klaim" />
            <SortTh label="Disetujui" sortKeyName="disetujui" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="cb-claimdet-sort-disetujui" />
            <th className="text-center px-3 py-2">Status</th>
            <th className="text-left px-3 py-2 min-w-[200px]">Catatan</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50">
          {sorted.map((it) => (
            <tr key={it.id} data-testid={`cb-claimdet-item-row-${it.imei}`} className={it.status === "rejected" ? "bg-rose-50/40" : ""}>
              <td className="px-3 py-2 font-mono text-xs">{it.imei}</td>
              {programBerKanal && <td className="px-3 py-2 font-mono text-xs">{it.serial_number || "-"}</td>}
              <td className="px-3 py-2">
                <p className="text-slate-900">{it.nama_produk}</p>
                <p className="text-[10px] text-slate-500 font-mono">{it.sku}</p>
              </td>
              {programBerKanal && (
                <td className="px-3 py-2 text-xs">
                  <p>{it.tanggal_terjual ? formatTanggal(it.tanggal_terjual) : "-"}</p>
                  <p className="text-[10px] text-slate-500">{(it.kanal_program === KANAL_DEALER ? it.nama_pelanggan : it.nama_cabang) || ""}</p>
                </td>
              )}
              <td className="px-3 py-2 text-right font-mono">{idr(it.cashback_nominal)}</td>
              <td className="px-3 py-2 text-right font-mono">{it.approved_nominal != null ? idr(it.approved_nominal) : "-"}</td>
              <td className="px-3 py-2 text-center"><ItemStatusPill status={it.status} /></td>
              <td className="px-3 py-2 text-[11px] text-slate-600">
                {it.status === "rejected" && it.rejection_reason && (
                  <p className="text-rose-700"><span className="font-semibold">Alasan tolak:</span> {it.rejection_reason}</p>
                )}
                {it.reclaimed_in_item_id && <p className="text-sky-700">Sudah di-reclaim ke batch baru</p>}
                {it.reclaim_of_item_id && <p className="text-slate-500">Hasil reclaim dari klaim sebelumnya</p>}
                <p className="text-slate-400 mt-0.5" title="Eligibility saat IMEI ini dimasukkan ke batch">{it.eligibility_alasan_saat_klaim}</p>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

// ============ Tab: Settlement (brief butir 17-18) ============
function SettlementTab({ claim, canDelete, onVoid, navigate }) {
  // Urutan bawaan: settlement terbaru dulu. `settlements` adalah array
  // NESTED di dokumen klaim (bukan hasil query terpotong) — aman disortir
  // tanpa BatasDaftarNotice.
  const defaultRows = [...(claim.settlements || [])].sort((a, b) => (b.at || "").localeCompare(a.at || ""));
  const { sortKey, sortDir, toggle } = useSortState(null);
  const rows = sortRows(defaultRows, sortKey, sortDir, {
    tanggal: (s) => s.tanggal || s.at || "",
    metode: (s) => s.metode || "",
    nominal: (s) => s.nominal || 0,
    oleh: (s) => s.by || "",
  });
  if (rows.length === 0) return <p className="text-sm text-slate-400 py-8 text-center">Belum ada settlement tercatat.</p>;
  return (
    <div className="border border-slate-200 rounded-lg overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
          <tr>
            <SortTh label="Tanggal" sortKeyName="tanggal" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="cb-settle-sort-tanggal" />
            <SortTh label="Metode" sortKeyName="metode" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="cb-settle-sort-metode" />
            <SortTh label="Nominal" sortKeyName="nominal" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="cb-settle-sort-nominal" />
            <th className="text-left px-3 py-2">Keterangan</th>
            <SortTh label="Oleh" sortKeyName="oleh" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="cb-settle-sort-oleh" />
            <th className="text-center px-3 py-2">Status</th>
            <th className="text-right px-3 py-2">Aksi</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50">
          {rows.map((s) => (
            <tr key={s.id} data-testid={`cb-settle-row-${s.id}`} className={s.voided ? "opacity-50" : ""}>
              <td className="px-3 py-2">{formatTanggal(s.tanggal)}</td>
              <td className="px-3 py-2">
                {METODE_LABEL_LOCAL[s.metode] || s.metode}
                {s.credit_note_code && <p className="text-[10px] font-mono text-slate-500">{s.credit_note_code}</p>}
              </td>
              <td className="px-3 py-2 text-right font-mono">{idr(s.nominal)}</td>
              <td className="px-3 py-2 text-xs text-slate-600">{s.keterangan || "-"}</td>
              <td className="px-3 py-2 text-xs">{s.by_name || s.by}</td>
              <td className="px-3 py-2 text-center">
                {s.voided
                  ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">Voided</span>
                  : <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">Aktif</span>}
              </td>
              <td className="px-3 py-2 text-right">
                {!s.voided && s.metode === "credit_note" ? (
                  <button onClick={() => navigate(s.credit_note_id ? `/finance/credit-note/${s.credit_note_id}` : "/finance/credit-note")}
                          data-testid={`cb-settle-cn-link-${s.id}`} className="text-sky-700 hover:text-sky-900 text-xs font-medium inline-flex items-center gap-1">
                    Kelola di Credit Note <ExternalLink className="w-3 h-3" />
                  </button>
                ) : !s.voided && canDelete ? (
                  <button onClick={() => onVoid(s)} data-testid={`cb-settle-void-${s.id}`} className="text-rose-600 hover:text-rose-800 text-xs font-medium">Void</button>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const METODE_LABEL_LOCAL = {
  credit_note: "Credit Note (CN)",
  kas_bank: "Kas/Bank Langsung",
  potong_utang_langsung: "Potong Utang Langsung",
};

// ============ Modal: Submit ke Supplier ============
function SubmitModal({ claim, onClose, onDone }) {
  const [f, setF] = useState({ tanggal_submit: hariIniISO(), catatan: "" });
  const [busy, setBusy] = useState(false);
  const lewatDeadline = f.tanggal_submit > claim.deadline_submit_saat_dibuat;
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`${CB_BASE}/claims/${claim.id}/submit`, f);
      toast.success(r.data.message); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal submit Claim Batch"); }
    finally { setBusy(false); }
  };
  return (
    <Modal title="Submit ke Supplier" icon={Send} onClose={onClose} onSubmit={submit} submitLabel="Submit Claim Batch" busy={busy} testid="cb-claimdet-submit-modal">
      <p className="text-sm text-slate-600">{claim.jumlah_item} IMEI · Total klaim <b>{idr(claim.total_klaim)}</b> akan disubmit ke {claim.supplier_nama}.</p>
      <Field label="Tanggal Submit"><DatePicker value={f.tanggal_submit} onChange={(v) => setF({ ...f, tanggal_submit: v })} data-testid="cb-claimdet-submit-tgl" /></Field>
      {lewatDeadline && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 inline-flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> Tanggal ini SUDAH LEWAT deadline program ({formatTanggal(claim.deadline_submit_saat_dibuat)}) — batch tetap bisa disubmit, tapi supplier berhak menolak karena lewat batas waktu.
        </div>
      )}
      <Field label="Catatan (opsional)"><textarea rows={2} value={f.catatan} onChange={(e) => setF({ ...f, catatan: e.target.value })} data-testid="cb-claimdet-submit-catatan" className={inputCls + " h-auto py-2"} /></Field>
    </Modal>
  );
}

// ============ Modal: Kelola IMEI (tambah/keluarkan) ============
function KelolaImeiModal({ claim, items, onClose, onDone }) {
  const aktif = items.filter((i) => i.klaim_aktif);
  const [hapus, setHapus] = useState(() => new Set());
  const [kandidat, setKandidat] = useState([]);
  const [loadingKandidat, setLoadingKandidat] = useState(true);
  const [cari, setCari] = useState("");
  const [tambah, setTambah] = useState(() => new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`${CB_BASE}/programs/${claim.program_id}/kandidat`, { params: { status: "eligible" } })
      .then((r) => setKandidat(r.data.items || []))
      .catch(() => toast.error("Gagal memuat IMEI eligible tambahan"))
      .finally(() => setLoadingKandidat(false));
  }, [claim.program_id]);

  const kandidatFiltered = useMemo(() => {
    const t = cari.trim().toLowerCase();
    if (!t) return kandidat;
    return kandidat.filter((k) => k.imei.toLowerCase().includes(t) || k.sku.toLowerCase().includes(t) || (k.nama_produk || "").toLowerCase().includes(t));
  }, [kandidat, cari]);

  const toggleHapus = (imei) => setHapus((prev) => { const n = new Set(prev); n.has(imei) ? n.delete(imei) : n.add(imei); return n; });
  const toggleTambah = (imei) => setTambah((prev) => { const n = new Set(prev); n.has(imei) ? n.delete(imei) : n.add(imei); return n; });

  const submit = async () => {
    if (hapus.size === 0 && tambah.size === 0) return onClose();
    setBusy(true);
    try {
      const r = await api.post(`${CB_BASE}/claims/${claim.id}/items`, {
        tambah_imeis: [...tambah], hapus_imeis: [...hapus],
      });
      // Sama seperti BuatClaimModal — IMEI di luar periode klaim TETAP
      // ditambahkan (permintaan bos 21 Agu E1), tapi toast MERAH supaya
      // langsung kelihatan, bukan toast sukses biasa.
      if (r.data.di_luar_periode_klaim) toast.error(r.data.message); else toast.success(r.data.message);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengubah daftar IMEI"); }
    finally { setBusy(false); }
  };

  return (
    // `dangerous` — dua daftar checkbox dinamis (keluarkan/tambah IMEI); kalau
    // tersentuh tutup tanpa sadar, pilihan yang sudah dicentang hilang semua.
    <Modal title="Kelola IMEI dalam Batch" icon={PlusCircle} onClose={onClose} onSubmit={submit}
           submitLabel="Simpan Perubahan" busy={busy} testid="cb-claimdet-items-modal" wide
           dangerous>
      <div>
        <p className="text-sm font-semibold text-slate-800 mb-2">IMEI dalam batch ini ({aktif.length})</p>
        <div className="border border-slate-200 rounded-lg max-h-40 overflow-y-auto divide-y divide-slate-100">
          {aktif.map((it) => (
            <div key={it.id} className="px-3 py-1.5 flex items-center justify-between text-xs">
              <div className="flex items-center gap-2">
                <span className="font-mono">{it.imei}</span><span className="text-slate-500">{it.sku}</span>
                <ItemStatusPill status={it.status} />
              </div>
              <div className="flex items-center gap-2">
                <span className="font-mono">{idr(it.cashback_nominal)}</span>
                {["draft", "submitted"].includes(it.status) && (
                  <button onClick={() => toggleHapus(it.imei)} data-testid={`cb-claimdet-items-del-${it.imei}`}
                          className={hapus.has(it.imei) ? "text-rose-700" : "text-rose-400 hover:text-rose-700"} title="Keluarkan dari batch">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
        {hapus.size > 0 && <p className="text-[11px] text-rose-600 mt-1">{hapus.size} IMEI akan dikeluarkan dari batch.</p>}
      </div>

      <div className="pt-2 border-t border-slate-100">
          <p className="text-sm font-semibold text-slate-800 mb-2">Tambah IMEI Eligible Lainnya</p>
          <div className="relative mb-2">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={cari} onChange={(e) => setCari(e.target.value)} placeholder="Cari IMEI/SKU/produk..." data-testid="cb-claimdet-items-cari"
                   className="w-full h-9 pl-9 pr-3 border border-slate-200 rounded-lg text-sm" />
          </div>
          <div className="border border-slate-200 rounded-lg max-h-48 overflow-y-auto divide-y divide-slate-100">
            {loadingKandidat ? (
              <p className="p-3 text-xs text-slate-400">Memuat IMEI eligible...</p>
            ) : kandidatFiltered.length === 0 ? (
              <p className="p-3 text-xs text-slate-400">Tidak ada IMEI eligible tambahan untuk program ini.</p>
            ) : kandidatFiltered.map((k) => (
              <label key={k.imei} className="px-3 py-1.5 flex items-center justify-between text-xs cursor-pointer hover:bg-slate-50">
                <span className="flex items-center gap-2">
                  <input type="checkbox" checked={tambah.has(k.imei)} onChange={() => toggleTambah(k.imei)} data-testid={`cb-claimdet-items-add-${k.imei}`} />
                  <span className="font-mono">{k.imei}</span><span className="text-slate-500">{k.sku}</span>
                </span>
                <span className="font-mono">{idr(k.cashback_nominal)}</span>
              </label>
            ))}
          </div>
          {tambah.size > 0 && <p className="text-[11px] text-emerald-700 mt-1">{tambah.size} IMEI akan ditambahkan ke batch.</p>}
      </div>
    </Modal>
  );
}

// ============ Modal: Putuskan Approve/Reject (brief butir 14-15, approval PARSIAL) ============
function PutuskanModal({ claim, items, aktivasi, onClose, onDone }) {
  const [decisions, setDecisions] = useState(() => Object.fromEntries(items.map((it) => [it.id, { mode: null, approved_nominal: it.cashback_nominal, alasan_tolak: "" }])));
  const [busy, setBusy] = useState(false);
  const [busyImpor, setBusyImpor] = useState(false);
  const [hasilImpor, setHasilImpor] = useState(null);
  // item_id -> catatan pencocokan dari file supplier (penanda "diisi dari file").
  const [dariFile, setDariFile] = useState({});

  const setDecision = (id, patch) => setDecisions((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  // Brief A.3 + keputusan A12: file "yang berhasil claim" dari supplier HANYA
  // mengisi pilihan di jendela ini — backend `impor-persetujuan` tidak menulis
  // apa pun, dan keputusan tetap tersimpan lewat tombol Simpan (`/approve`, batas
  // nominal <= klaim yang sama). IMEI yang tidak ada di file TIDAK ditolak
  // otomatis: penolakan keliru menghilangkan cashback yang sah.
  const imporPersetujuan = async (file) => {
    if (!file) return;
    setBusyImpor(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`${CB_BASE}/claims/${claim.id}/impor-persetujuan`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const cocok = r.data.cocok || [];
      setDecisions((prev) => {
        const n = { ...prev };
        cocok.forEach((c) => {
          if (n[c.item_id]) n[c.item_id] = { ...n[c.item_id], mode: "approve", approved_nominal: c.approved_nominal };
        });
        return n;
      });
      setDariFile((prev) => {
        const n = { ...prev };
        cocok.forEach((c) => { n[c.item_id] = c.catatan || "cocok dengan file supplier"; });
        (r.data.perlu_dicek || []).forEach((c) => { n[c.item_id] = `perlu dicek: ${c.alasan}`; });
        return n;
      });
      setHasilImpor(r.data);
      toast.success(r.data.message);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal membaca file persetujuan supplier"); }
    finally { setBusyImpor(false); }
  };

  const keputusan = items
    .filter((it) => decisions[it.id]?.mode)
    .map((it) => {
      const d = decisions[it.id];
      return d.mode === "approve"
        ? { imei: it.imei, keputusan: "approve", approved_nominal: Number(d.approved_nominal) }
        : { imei: it.imei, keputusan: "reject", alasan_tolak: d.alasan_tolak.trim() };
    });
  const valid = keputusan.length > 0 && keputusan.every((k) =>
    k.keputusan === "approve" ? k.approved_nominal > 0 : k.alasan_tolak.length > 0);

  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      const r = await api.post(`${CB_BASE}/claims/${claim.id}/approve`, { keputusan });
      toast.success(r.data.message); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan keputusan"); }
    finally { setBusy(false); }
  };

  return (
    // `dangerous` — daftar IMEI dinamis dengan nominal disetujui yang bisa
    // diketik ulang per baris; keputusan yang belum disimpan gampang hilang
    // kalau modal ini tertutup tak sengaja.
    <Modal title="Putuskan Approve / Reject per IMEI" icon={CheckCircle2} onClose={onClose} onSubmit={submit}
           submitLabel={`Simpan ${keputusan.length} Keputusan`} busy={busy} disabled={!valid} testid="cb-claimdet-approve-modal" wide
           dangerous>
      <p className="text-xs text-slate-500">Boleh diputuskan sebagian — sisanya bisa diputuskan lain kali lewat tombol yang sama.</p>
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2" data-testid="cb-approve-impor">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <p className="text-xs font-semibold text-slate-700 inline-flex items-center gap-1.5"><FileCheck2 className="w-3.5 h-3.5" /> Impor data persetujuan dari supplier</p>
            <p className="text-[11px] text-slate-500 mt-0.5">File .xlsx berisi IMEI (dan/atau Serial Number, nominal disetujui opsional). Yang cocok otomatis terpilih "Setujui" — periksa dulu, lalu tekan Simpan.</p>
          </div>
          <label className={`h-8 px-3 rounded-lg bg-slate-800 hover:bg-slate-900 text-white text-xs font-medium inline-flex items-center gap-1.5 ${busyImpor ? "opacity-60" : "cursor-pointer"}`}>
            <Upload className="w-3.5 h-3.5" /> {busyImpor ? "Membaca..." : "Impor .xlsx"}
            <input type="file" accept=".xlsx" className="hidden" data-testid="cb-approve-impor-file" disabled={busyImpor}
                   onChange={(e) => { imporPersetujuan(e.target.files?.[0]); e.target.value = ""; }} />
          </label>
        </div>
        {hasilImpor && (
          <div className="text-[11px] bg-white border border-slate-200 rounded-lg p-2 space-y-1" data-testid="cb-approve-impor-hasil">
            <p className="text-slate-700">
              <b>{(hasilImpor.cocok || []).length}</b> IMEI cocok (terpilih Setujui) · {(hasilImpor.perlu_dicek || []).length} perlu dicek ·
              {" "}{(hasilImpor.tidak_cocok || []).length} baris file tidak cocok · {(hasilImpor.tidak_ada_di_file || []).length} IMEI batch tidak ada di file
              {(hasilImpor.sudah_diputuskan || []).length ? ` · ${hasilImpor.sudah_diputuskan.length} sudah diputuskan sebelumnya` : ""}
            </p>
            {(hasilImpor.tidak_cocok || []).length > 0 && (
              <div className="max-h-24 overflow-y-auto">
                {hasilImpor.tidak_cocok.map((t, i) => (
                  <p key={i} className="text-rose-700">Baris {t.baris}: {t.imei || t.sn || "-"} — {t.alasan}</p>
                ))}
              </div>
            )}
            {(hasilImpor.tidak_ada_di_file || []).length > 0 && (
              <p className="text-slate-500">Tidak ada di file (TIDAK ditolak otomatis — putuskan manual): {hasilImpor.tidak_ada_di_file.slice(0, 15).join(", ")}{hasilImpor.tidak_ada_di_file.length > 15 ? ", ..." : ""}</p>
            )}
          </div>
        )}
      </div>
      <div className="space-y-2 max-h-[55vh] overflow-y-auto">
        {items.map((it) => {
          const d = decisions[it.id];
          return (
            <div key={it.id} data-testid={`cb-approve-row-${it.imei}`} className={`border rounded-lg p-3 ${dariFile[it.id] ? "border-emerald-300 bg-emerald-50/30" : "border-slate-200"}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="font-mono text-xs">{it.imei} <span className="text-slate-500 font-sans">· {it.sku}</span>{it.serial_number ? <span className="text-slate-500"> · SN {it.serial_number}</span> : null}</p>
                  <p className="text-[11px] text-slate-500">{aktivasi ? "Support Aktivasi" : "Klaim"} {idr(it.cashback_nominal)}</p>
                  {dariFile[it.id] && <p className="text-[11px] text-emerald-700" data-testid={`cb-approve-darifile-${it.imei}`}>Dari file supplier: {dariFile[it.id]}</p>}
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => setDecision(it.id, { mode: "approve" })} data-testid={`cb-approve-btn-approve-${it.imei}`}
                          className={`h-8 px-3 rounded-lg border text-xs font-medium ${d.mode === "approve" ? "bg-emerald-600 border-emerald-600 text-white" : "border-slate-200 text-slate-600 hover:bg-emerald-50"}`}>
                    Setujui
                  </button>
                  <button onClick={() => setDecision(it.id, { mode: "reject" })} data-testid={`cb-approve-btn-reject-${it.imei}`}
                          className={`h-8 px-3 rounded-lg border text-xs font-medium ${d.mode === "reject" ? "bg-rose-600 border-rose-600 text-white" : "border-slate-200 text-slate-600 hover:bg-rose-50"}`}>
                    Tolak
                  </button>
                  {d.mode && (
                    <button onClick={() => setDecision(it.id, { mode: null })} data-testid={`cb-approve-btn-nanti-${it.imei}`}
                            className="h-8 px-2 rounded-lg text-xs text-slate-400 hover:text-slate-700">Batal pilih</button>
                  )}
                </div>
              </div>
              {d.mode === "approve" && (
                <div className="mt-2">
                  <label className="block text-[11px] text-slate-500 mb-1">Nominal Disetujui (maks. {idr(it.cashback_nominal)})</label>
                  <InputRupiah value={d.approved_nominal} onChange={(n) => setDecision(it.id, { approved_nominal: n })}
                               testid={`cb-approve-nominal-${it.imei}`} className={inputCls + " max-w-xs"} />
                </div>
              )}
              {d.mode === "reject" && (
                <div className="mt-2">
                  <label className="block text-[11px] text-slate-500 mb-1">Alasan Penolakan (wajib)</label>
                  <textarea rows={2} value={d.alasan_tolak} onChange={(e) => setDecision(it.id, { alasan_tolak: e.target.value })}
                            data-testid={`cb-approve-alasan-${it.imei}`} className={inputCls + " h-auto py-1.5"} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Modal>
  );
}

// ============ Modal: Settlement (brief butir 17-18, 3 metode) ============
function SettlementModal({ claim, outstanding, jenisBatch, onClose, onDone }) {
  const { can } = useAuth();
  // R13b: settlement lewat Credit Note memanggil fungsi CN langsung, jadi backend
  // menegakkan `finance_credit_note.create` + `.update` SENDIRI (403 tanpa
  // keduanya). Pilihan metodenya disembunyikan dengan kunci yang SAMA (aturan #3).
  const bisaCN = can("finance_credit_note", "create") && can("finance_credit_note", "update");
  const aktivasi = jenisBatch === JENIS_AKTIVASI;
  // A10: kategori CN bawaan menurut jenis program — "Support Aktivasi" untuk
  // program aktivasi, "Price Protection" selainnya. Kategori silang DITOLAK
  // backend, jadi tidak ditawarkan di sini.
  const kategoriTerlarang = aktivasi ? "Price Protection" : "Support Aktivasi";
  const kategoriBawaan = aktivasi ? "Support Aktivasi" : "Price Protection";
  const [metode, setMetode] = useState("");
  const [f, setF] = useState({ tanggal: hariIniISO(), nominal: outstanding, keterangan: "" });
  const [kasBank, setKasBank] = useState([]);
  const [akunId, setAkunId] = useState("");
  const [kategoriOpts, setKategoriOpts] = useState([]);
  const [kategoriCn, setKategoriCn] = useState("");
  const [utangList, setUtangList] = useState([]);
  const [loadingUtang, setLoadingUtang] = useState(false);
  const [searchUtang, setSearchUtang] = useState(claim.supplier_nama);
  const [utangSelected, setUtangSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (metode === "kas_bank" && kasBank.length === 0) {
      api.get("/finance/accounts/kas-bank").then((r) => setKasBank(r.data || [])).catch(() => {});
    }
    if (metode === "credit_note" && kategoriOpts.length === 0) {
      // `kategoriCn` kosong = "bawaan menurut jenis" — backend yang memilih (dan
      // membuat kategori "Support Aktivasi" kalau basis data lama belum punya).
      api.get("/credit-note/kategori-options").then((r) => {
        const opts = (r.data.kategori || []).filter((k) => k.toLowerCase() !== kategoriTerlarang.toLowerCase()
          && k.toLowerCase() !== kategoriBawaan.toLowerCase());
        setKategoriOpts(opts);
      }).catch(() => {});
    }
    if (metode === "potong_utang_langsung" && utangList.length === 0) {
      setLoadingUtang(true);
      api.get("/finance/utang", { params: { status: "aktif" } })
        .then((r) => setUtangList(r.data.items || []))
        .catch(() => toast.error("Gagal memuat daftar utang aktif"))
        .finally(() => setLoadingUtang(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metode]);

  const utangFiltered = useMemo(() => {
    const term = searchUtang.trim().toLowerCase();
    return utangList
      .filter((u) => ((u.nominal || 0) - (u.nominal_terbayar || 0)) > 0.5)
      .filter((u) => !term || (u.supplier_nama || "").toLowerCase().includes(term) || (u.code || "").toLowerCase().includes(term))
      .slice(0, 30);
  }, [utangList, searchUtang]);

  const valid = !!metode && Number(f.nominal) > 0 && Number(f.nominal) <= outstanding + 0.5 &&
    (metode !== "kas_bank" || !!akunId) &&
    (metode !== "potong_utang_langsung" || !!utangSelected) &&
    (metode !== "credit_note" || bisaCN);

  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      const body = { metode, nominal: Number(f.nominal), tanggal: f.tanggal, keterangan: f.keterangan };
      if (metode === "kas_bank") body.akun_id = akunId;
      if (metode === "potong_utang_langsung") body.utang_id = utangSelected.id;
      if (metode === "credit_note" && kategoriCn) body.kategori_cn = kategoriCn;
      const r = await api.post(`${CB_BASE}/claims/${claim.id}/settlement`, body);
      toast.success(r.data.message); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mencatat settlement"); }
    finally { setBusy(false); }
  };

  return (
    // `dangerous` — nominal settlement diketik/dipilih staf sendiri (InputRupiah),
    // plus pilihan metode & akun/utang yang gampang hilang kalau tertutup tak sengaja.
    <Modal title="Catat Settlement" icon={Wallet} onClose={onClose} onSubmit={submit} submitLabel="Simpan Settlement"
           busy={busy} disabled={!valid} testid="cb-claimdet-settlement-modal" wide
           dangerous>
      <p className="text-sm text-slate-600">Outstanding batch ini: <b>{idr(outstanding)}</b></p>
      <Field label="Metode Settlement">
        <select value={metode} onChange={(e) => setMetode(e.target.value)} data-testid="cb-settle-metode" className={inputCls}>
          <option value="">Pilih metode...</option>
          {bisaCN && <option value="credit_note">Credit Note (CN)</option>}
          <option value="kas_bank">Kas/Bank Langsung</option>
          <option value="potong_utang_langsung">Potong Utang Langsung</option>
        </select>
      </Field>
      {!bisaCN && (
        <p className="text-[11px] text-slate-500" data-testid="cb-settle-cn-tanpa-izin">
          Metode Credit Note butuh izin Credit Note (buat & ubah) — minta pemegang izin itu memproses kalau supplier menerbitkan CN.
        </p>
      )}

      {metode === "credit_note" && (
        <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 text-xs text-sky-800">
          Credit Note akan diterbitkan OTOMATIS untuk supplier ini dan langsung diserahkan — bisa ditelusuri lengkap di menu <b>Finance &gt; Credit Note</b> (aging, pencairan, pemakaian).
        </div>
      )}
      {metode === "kas_bank" && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-700">
          Uang diterima LANGSUNG dari supplier tanpa dokumen CN formal (mis. transfer langsung) — dijurnal ke akun kas/bank yang dipilih.
        </div>
      )}
      {metode === "potong_utang_langsung" && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-700">
          Memotong TAGIHAN utang supplier yang sama tanpa uang keluar — hanya bisa memilih utang milik supplier <b>{claim.supplier_nama}</b>.
        </div>
      )}

      {metode && (
        <div className="grid grid-cols-2 gap-3">
          <Field label="Tanggal"><DatePicker value={f.tanggal} onChange={(v) => setF({ ...f, tanggal: v })} data-testid="cb-settle-tgl" /></Field>
          <Field label="Nominal" hint={`Maks. ${idr(outstanding)}`}>
            <InputRupiah value={f.nominal} onChange={(n) => setF({ ...f, nominal: n })} testid="cb-settle-nominal" className={inputCls} />
          </Field>
        </div>
      )}

      {metode === "credit_note" && (
        <Field label="Kategori CN" hint={`Bawaan untuk ${aktivasi ? "program Support Aktivasi" : "program Cashback"}: "${kategoriBawaan}".`}>
          <select value={kategoriCn} onChange={(e) => setKategoriCn(e.target.value)} data-testid="cb-settle-kategori-cn" className={inputCls}>
            <option value="">{kategoriBawaan} (bawaan)</option>
            {kategoriOpts.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </Field>
      )}

      {metode === "kas_bank" && (
        <Field label="Masuk ke Akun">
          <select value={akunId} onChange={(e) => setAkunId(e.target.value)} data-testid="cb-settle-akun" className={inputCls}>
            <option value="">Pilih...</option>
            {kasBank.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </Field>
      )}

      {metode === "potong_utang_langsung" && (
        <div>
          <Field label="Cari Utang Aktif (nama supplier / kode)">
            <input value={searchUtang} onChange={(e) => setSearchUtang(e.target.value)} data-testid="cb-settle-utang-search" className={inputCls} />
          </Field>
          <div className="border border-slate-200 rounded-lg max-h-40 overflow-y-auto divide-y divide-slate-100 mt-1.5">
            {loadingUtang ? (
              <p className="p-3 text-xs text-slate-400">Memuat utang aktif...</p>
            ) : utangFiltered.length === 0 ? (
              <p className="p-3 text-xs text-slate-400">Tidak ada utang aktif yang cocok.</p>
            ) : utangFiltered.map((u) => {
              const sisa = (u.nominal || 0) - (u.nominal_terbayar || 0);
              return (
                <button type="button" key={u.id} onClick={() => setUtangSelected(u)} data-testid={`cb-settle-utang-${u.id}`}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 ${utangSelected?.id === u.id ? "bg-emerald-50" : ""}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-slate-900 truncate">{u.supplier_nama}</span>
                    <span className="font-mono text-xs text-slate-700 shrink-0">{idr(sisa)}</span>
                  </div>
                  <p className="text-[11px] text-slate-500">{u.code}</p>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {metode && <Field label="Keterangan (opsional)"><input value={f.keterangan} onChange={(e) => setF({ ...f, keterangan: e.target.value })} data-testid="cb-settle-ket" className={inputCls} /></Field>}
    </Modal>
  );
}

// ============ Modal: Reclaim IMEI Ditolak (brief butir 16) ============
function ReclaimModal({ claim, items, onClose, onDone }) {
  const [selected, setSelected] = useState(() => new Set(items.map((i) => i.imei)));
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const toggle = (imei) => setSelected((prev) => { const n = new Set(prev); n.has(imei) ? n.delete(imei) : n.add(imei); return n; });
  const valid = selected.size > 0 && reason.trim().length > 0;

  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      const r = await api.post(`${CB_BASE}/claims/${claim.id}/reclaim`, { reason: reason.trim(), imeis: [...selected] });
      toast.success(r.data.message); onDone(r.data.item);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal reclaim IMEI"); }
    finally { setBusy(false); }
  };

  return (
    // `dangerous` — daftar checkbox dinamis (banyak IMEI bisa dicentang/dilepas)
    // plus alasan reclaim yang wajib diisi; bukan sekadar satu baris alasan.
    <Modal title="Reclaim IMEI yang Ditolak" icon={RefreshCcw} onClose={onClose} onSubmit={submit}
           submitLabel={`Buat Batch Baru (${selected.size} IMEI)`} busy={busy} disabled={!valid} testid="cb-claimdet-reclaim-modal" wide
           dangerous>
      <p className="text-xs text-slate-500">IMEI terpilih dipindahkan ke Claim Batch BARU. Item lama di batch ini tetap tersimpan sebagai jejak audit (statusnya tetap "Ditolak").</p>
      <div className="border border-slate-200 rounded-lg max-h-48 overflow-y-auto divide-y divide-slate-100">
        {items.map((it) => (
          <label key={it.id} className="px-3 py-2 flex items-center justify-between text-sm cursor-pointer hover:bg-slate-50">
            <span className="flex items-center gap-2">
              <input type="checkbox" checked={selected.has(it.imei)} onChange={() => toggle(it.imei)} data-testid={`cb-reclaim-cb-${it.imei}`} />
              <span className="font-mono text-xs">{it.imei}</span><span className="text-slate-500 text-xs">{it.sku}</span>
            </span>
            <span className="text-right">
              <span className="font-mono text-xs block">{idr(it.cashback_nominal)}</span>
              <span className="text-[10px] text-rose-600">{it.rejection_reason}</span>
            </span>
          </label>
        ))}
      </div>
      <Field label="Alasan Reclaim (wajib)">
        <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="cb-reclaim-alasan" className={inputCls + " h-auto py-2"} />
      </Field>
    </Modal>
  );
}
